import * as Yup from "yup";
import { t } from "@lingui/macro";
import { i18n } from "../../../i18n";

Yup.setLocale({
  string: {
    default: i18n._(t("validation.must_be_a_string")`Must be a string`),
  },
  number: {
    default: i18n._(t("validation.must_be_a_number")`Must be a number`),
  },
});

/**
 * The Yup schema object that Formik will use to validate the form's state
 */
const defaultActionSchema = () => ({
  summary: Yup.string().required(
    i18n._(
      t(
        "action_form.validation.summary_cannot_be_blank"
      )`Summary cannot be blank`
    )
  ),
  description: Yup.string(),
  assignee: Yup.string(),
  skysparkLink: Yup.string().url(
    i18n._(
      t(
        "action_form.validation.report_url_must_be_a_valid_url"
      )`Report URL must be a valid URL`
    )
  ),
  elec: Yup.number().min(
    0,
    i18n._(
      t(
        "action_form.validation.electricity_savings_greater_or_equal_to_0"
      )`Electricity savings must be a number greater than or equal to 0`
    )
  ),
  water: Yup.number().min(
    0,
    i18n._(
      t(
        "action_form.validation.water_savings_greater_or_equal_to_0"
      )`Water savings must be a number greater than or equal to 0`
    )
  ),
  gas: Yup.number().min(
    0,
    i18n._(
      t(
        "action_form.validation.gas_savings_greater_or_equal_to_0"
      )`Gas savings must be a number greater than or equal to 0`
    )
  ),
  totalFinancialImpact: Yup.number().min(
    0,
    i18n._(
      t(
        "action_form.validation.total_financial_impact_greater_or_equal_to_0"
      )`Total financial impact must be a number greater than or equal to 0`
    )
  ),
  operationalSavings: Yup.number().min(
    0,
    i18n._(
      t(
        "action_form.validation.operational_savings_greater_or_equal_to_0"
      )`Operational savings must be a number greater than or equal to 0`
    )
  ),
  rule: Yup.string(),
  context: Yup.string().required(
    i18n._(
      t(
        "action_form.validation.context_cannot_be_blank"
      )`Context cannot be blank`
    )
  ),
  priority: Yup.string().required(
    i18n._(
      t(
        "action_form.validation.priority_cannot_be_blank"
      )`Priority cannot be blank`
    )
  ),
  status: Yup.string().required(
    i18n._(
      t("action_form.validation.status_cannot_be_blank")`Status cannot be blank`
    )
  ),
  responsible: Yup.string().required(
    i18n._(
      t(
        "action_form.validation.responsible_cannot_be_blank"
      )`Responsible cannot be blank`
    )
  ),
});

export const addActionSchema = () =>
  Yup.object().shape({
    ...defaultActionSchema(),
    site: Yup.string().required(
      i18n._(
        t("action_form.validation.site_cannot_be_blank")`Site cannot be blank`
      )
    ),
  });

export const editActionSchema = () =>
  Yup.object().shape({
    ...defaultActionSchema(),
    comment: Yup.string(),
    transitionReason: Yup.string(),
  });

/**
 * Used by formik to map some initial blank props
 */
export function mapAddActionToProps(props) {
  const rule = props.fromAssociate ? props.fromAssociate.rule : "";
  const site = props.fromAssociate ? props.fromAssociate.site : "";

  return {
    site,
    summary: rule, // NOTE: Defaulting to the rule name if one is supplied
    description: "",
    skysparkLink: "",
    assignee: "",
    elec: "", // NOTE: number
    water: "", // NOTE: number
    gas: "", // NOTE: number
    totalFinancialImpact: "", // NOTE: number
    operationalSavings: "", // NOTE: number
    rule,
    context: "",
    priority: "",
    status: "",
    responsible: "",
    screenshot: null,
    screenshotLink: "",
  };
}

/**
 * Used by formik to map the initial action to the form values
 */
export function mapEditActionToProps({ action }) {
  return {
    summary: action.summary,
    description: action.description,
    skysparkLink: action.skysparkLink || "",
    assignee: action.assignee || "",
    elec: !action.elec ? "" : Number(action.elec),
    water: !action.water ? "" : Number(action.water),
    gas: !action.gas ? "" : Number(action.gas),
    totalFinancialImpact: !action.totalFinancialImpact
      ? ""
      : Number(action.totalFinancialImpact),
    operationalSavings: !action.operationalSavings
      ? ""
      : Number(action.operationalSavings),
    rule: action.rule,
    context: action.context,
    priority: action.priority,
    status: action.status,
    responsible: action.responsible,
    screenshot: null,
    screenshotLink: !action.screenshotLink ? "" : action.screenshotLink,
    /** NOTE: Transition reason **always** defaults to empty string */
    transitionReason: "",
    comment: "",
  };
}

/** Formik options shared between create and edit */
export const formikOptions = {
  validateOnChange: true,
  validateOnBlur: true,
};
